import { Observable } from 'rxjs';
import { DeviceZigbeeCoordinator, GatewayDevice } from '../../../../app/@store';
import {
  DiverterValve,
  FeaturePropertyKey,
  StatusValueDcFeature,
} from './operational-data.constants';
import { HybridProPanelHelper } from 'src/app/+installations/installation/hybrid-pro-panel/hybrid-pro-panel.helper';

export interface Features {
  data: FeatureDeprecated[];
}

export interface DcFeatures {
  data: Feature<unknown, unknown>[];
}

export enum WebsocketDcFeaturesEventReason {
  TELEMETRY = 'TELEMETRY',
}

export enum WebsocketDcFeaturesEventType {
  FeatureChanged = 'feature.changed',
}

export interface WebsocketDcFeaturesEvent {
  event: typeof WebsocketDcFeaturesEventType;
  feature: Feature<unknown, unknown>;
  reason: keyof typeof WebsocketDcFeaturesEventReason;
}

export enum DcTypes {
  array = 'array',
  boolean = 'boolean',
  number = 'number',
  Schedule = 'Schedule',
  string = 'string',
  DeviceList = 'DeviceList',
  object = 'object',
}

export type FeatureProperties = {
  [key in FeaturePropertyKey]?: Property;
};

export interface Property {
  value:
    | boolean
    | string
    | number
    | SchedulePropertiesValue
    | GatewayDevice[]
    | DeviceBusTopology[]
    | FeatureProductMatrix
    | Array<boolean | string | number | FeatureProductMatrix | FeaturePowerLimitationOverview>
    | DeviceZigbeeCoordinator[]
    | DeviceMessageErrorsRawEntries[]
    | EmsPowerBalanceProperty[]
    | ConsolidatorDevice[]
    | EmsPowerBalanceProperty[]
    | EssStateBasicValues[]
    | EssStateExceptionValues[]
    | EssPhotovoltaicStateExceptionValues[]
    | EssPhotovoltaicStateBasicValues[]
    | BatteryInformationFeatures;
  type: keyof typeof DcTypes;
  description?: string;
  unit?: string;
  active?: ActiveHeatOnTime;
}

export type TypedFeatureProperties<T> = {
  [key in FeaturePropertyKey]?: TypedProperty<T>;
};

export interface TypedProperty<T> {
  value: T;
  type: keyof typeof DcTypes;
  description?: string;
  unit?: string;
}

export interface Feature<T extends FeatureProperties, M> {
  properties?: T;
  commands?: M;
  gatewayId: string;
  feature: string;
  timestamp: string;
  isEnabled: boolean;
  isReady: boolean;
  apiVersion?: number;
  components?: string[];
  uri?: string;
  deviceId?: string;
  value?: string;
  unit?: string;
}

/**
 * @deprecated This interface should not be used as it allows untyped properties, commands. Please use Feature<K, M> and define K, M. For lacking commands, use 'never'. Dont use ANY!
 */
export interface FeatureDeprecated<K = {}, M = {}> {
  properties: K;
  commands: M;
  gatewayId: string;
  feature: string;
  timestamp: string;
  isEnabled: boolean;
  isReady: boolean;
  apiVersion?: number;
  uri?: string;
  deviceId?: string;
}

export interface Command<P> {
  uri: string;
  name: string;
  isExecutable: boolean;
  params: P;
  description?: string;
}

export interface CommandParam<P> {
  type: 'string' | 'number';
  required: boolean;
  constraints: P;
  description?: string;
}

export interface ActivateDeactivateCommands {
  activate: Command<{}>;
  deactivate: Command<{}>;
}

export interface SrcCondensateAvoidanceCommands extends ActivateDeactivateCommands {
  setActivate: Command<{}>;
}

export interface RoomCommandParam {
  type: 'string' | 'number';
  required: boolean;
  description?: string;
}

export interface FixedSequenceStrategyCommandParam {
  type: 'string';
  required: boolean;
}

export interface BurnerRuntimeSequenceStrategyCommandParam {
  type: 'number';
  required: boolean;
  constraints: RangeConstraints;
}

export interface RangeConstraints {
  min: number;
  max: number;
  stepping: number;
}

export interface DhwRangeConstraints {
  min: number;
  max: number;
  stepping: number;
  efficientLowerBorder: number;
  efficientUpperBorder: number;
}

export interface PropertiesValue<T> {
  value: T;
}

export interface NumberValue {
  type: DcTypes.number;
  value: number;
  unit?: string;
  description?: string;
}

export interface StringValue {
  type: DcTypes.string;
  value: string;
  description?: string;
}

export interface ObjectValueGeneric<T> {
  type: DcTypes.object;
  value: T;
}

export interface StringValueGeneric<T> {
  type: DcTypes.string;
  value: T;
  description?: string;
}

export interface ArrayValue<V> {
  type: DcTypes.array | DcTypes.DeviceList;
  value: V[];
  unit?: string;
}

export interface BooleanValue {
  type: DcTypes.boolean;
  value: boolean;
  description?: string;
}

export interface TemperatureUnit {
  value: 'celsius';
  type: 'string';
}

export interface TemperatureValue {
  type: DcTypes.number;
  value: number;
  unit: string;
}

export interface RemoteControllerValue {
  type: DcTypes.number;
  value: number;
  unit: string;
}

export interface PartsPerMillionValue {
  value: number;
  type: DcTypes.number;
  description: string;
  unit: 'partsPerMillion';
}

export interface PartsPerMillionUnit {
  value: 'partsPerMillion';
  type: 'string';
}

export interface PercentValue {
  type: DcTypes.number;
  value: number;
  unit: 'percent';
}

export interface PercentUnit {
  value: 'percent';
  type: 'string';
}

export interface MinutesValue {
  type: 'number';
  value: number;
  unit: 'minutes';
}

export interface CubicMeterPerHourValue {
  type: DcTypes.number;
  value: number;
  unit: 'cubicMeter/hour';
}
export interface GenericNumberValueProperty {
  [key: string]: NumberValue;
}

export interface GenericStringValueProperty {
  [key: string]: StringValue;
}

export interface IsActiveProperty {
  active: BooleanValue;
}

export interface IsStringValueProperty {
  value: StringValue;
}

export interface IsNumberValueProperty {
  value: NumberValue;
}

export interface IsNumberBlockedProperty {
  blocked: NumberValue;
}

export interface GenericTemperatureProperty {
  [key: string]: NumberValue;
}

export interface TemperatureProperty {
  temperature: NumberValue;
}

export interface EssBatteryNVersionFirmware {
  build: NumberValue;
  family: NumberValue;
  revision: NumberValue;
  value: StringValue;
  version: NumberValue;
}

export interface DeviceFirmwareVersion {
  value: StringValue;
  build: NumberValue;
  family: NumberValue;
  revision: NumberValue;
  version: NumberValue;
}

export interface IsEnabledProperty {
  enabled: ArrayValue<string>;
}

export interface IsEnabledPropertyGeneric<T> {
  enabled: ArrayValue<T>;
}

// Feature Properties

// Gateway
export interface GatewayDevicesProperties {
  devices: ArrayValue<GatewayDevice>;
}

export interface GatewayProperties {
  online: BooleanValue;
}

export interface RoomsStatusProperties {
  active: BooleanValue;
  variant: StringValue;
}

export interface SrcPumpControlStatusProperties {
  active: BooleanValue;
}

export interface SrcCondensateAvoidanceStatusProperties {
  active: BooleanValue;
}

export interface GatewayCommands {
  reboot: Command<{ uri: string; name: string; isExecutable: BooleanValue }>;
}

export enum GatewayFirmwareUpdateStatus {
  IDLE = 'idle',
  DOWNLOADING = 'downloading',
  REBOOTING = 'rebooting',
  ERROR = 'error',
  UPDATED = 'updated',
}

export interface GatewayFirmwareProperties {
  version: StringValue;
  updateStatus: StringValueGeneric<GatewayFirmwareUpdateStatus>;
  error: StringValue;
  libraryVersion: StringValue;
  libraryType: StringValue;
}

export interface FeatureProductMatrix {
  busAddress: number;
  busType: string;
  productFamily: string;
  viessmannIdentificationNumber: string;
}

export interface ProductMatrixProperties {
  product: ArrayValue<FeatureProductMatrix>;
}

export interface ProductIdentificationProperties {
  product: ObjectValueGeneric<FeatureProductMatrix>;
}

export interface GatewayFirmwareCommands {
  update: Command<{
    version: CommandParam<{
      regEx: string;
    }>;
    url: CommandParam<{
      regEx: string;
    }>;
  }>;
}

export interface DemandRelayModeProperties {
  value: StringValueGeneric<FhtDemandRelayModeValues>;
}

// Remote Diagnostics
export interface RemoteDiagnosticsProperties {
  status: StringValue;
  webSocketUrl: StringValue;
}

export interface RemoteDiagnosticsCommands {
  open: {
    uri: string;
    name: string;
    isExecutable: boolean;
    params: {};
  };

  close: {
    uri: string;
    name: string;
    isExecutable: boolean;
    params: {};
  };
}

export interface ActiveModeCommands {
  setMode: Command<{
    mode: CommandParam<{
      enum: string[];
    }>;
  }>;
}

export interface ThermalCockpitProperties {
  electricityInput: NumberValue;
  thermalOutput: NumberValue;
}

// Heating Rod General
export interface HeatingRodGeneralProperties {
  useApproved: BooleanValue;
}

// Heating Rod Heating
export interface HeatingRodHeatingProperties {
  useApproved: BooleanValue;
}

export interface HeatingRodHeatingCommands {
  grant: Command<{}>;
  revoke: Command<{}>;
}

// Heating Rod Dhw
export interface HeatingRodDhwProperties {
  useApproved: BooleanValue;
}

// Heating Rod Status
export interface HeatingRodStatusProperties {
  overall: BooleanValue;
  level1: BooleanValue;
  level2: BooleanValue;
  level3: BooleanValue;
}

export interface HeatingRodStatisticsProperties {
  starts: NumberValue;
  hours: NumberValue;
}

export interface PowerLimitationOverviewProperties {
  limitations: ArrayValue<FeaturePowerLimitationOverview>;
}

export interface FeaturePowerLimitationOverview {
  deviceId: string;
  value: number;
  type: DcTypes.number;
  unit: string;
  method: string;
  limitationType: string;
}

// Program
export interface ProgramProperties {
  active?: BooleanValue;
  temperature: NumberValue;
}

export interface ProgramCommands {
  setTemperature: Command<{
    targetTemperature: CommandParam<RangeConstraints>;
  }>;
  activate: Command<{
    temperature: CommandParam<RangeConstraints>;
  }>;
  deactivate: Command<{}>;
}

export interface TemperatureLevelsPerceivedCommands {
  setTemperature: Command<{
    targetTemperature: CommandParam<RangeConstraints>;
  }>;
}

export interface FloorHeatingDamageProtectionThresholdCommands {
  setValue: Command<{
    value: CommandParam<RangeConstraints>;
  }>;
}

export interface OperatingProgramCommands {
  activate: Command<{
    temperature: CommandParam<RangeConstraints>;
  }>;
  deactivate: Command<{}>;
}

export interface OperatingProgramCommandsNormal {
  setTemperature: Command<{
    targetTemperature: CommandParam<RangeConstraints>;
  }>;
}

export interface OperatingProgramProperties {
  active: BooleanValue;
  demand?: StringValue;
  temperature: NumberValue;
}

export interface quickmodesProperties {
  active: BooleanValue;
}

export interface RenameHeatingCircuitProperties {
  name: StringValue;
}

export interface RenameHeatingCircuitCommands {
  setName: Command<{
    name: CommandParam<{
      minLength: number;
      maxLength: number;
    }>;
  }>;
}

export interface DeviceNameProperties {
  name: StringValue;
}

export interface DeviceNameCommands {
  setName: Command<{
    name: CommandParam<{
      minLength: number;
      maxLength: number;
    }>;
  }>;
}

export interface DeviceRemoteResetCommands {
  remoteReset: Command<{}>;
}

export interface OverarchingStandbyCommands {
  setActive: Command<{}>;
}

export interface HeatingCircuitCommands {
  setName: Command<{
    name: CommandParam<{
      minLength: number;
      maxLength: number;
    }>;
  }>;
}

export interface DeviceHeatingCircuitIdCommands {
  assignHeatingCircuit: Command<{
    value: CommandParam<RangeConstraints>;
  }>;
  unassignHeatingCircuit: Command<{}>;
}

// Holiday Program
export interface HolidayProgramProperties {
  active: BooleanValue;
  start: StringValue;
  end: StringValue;
}

export interface HolidayProgramCommands {
  changeEndDate: Command<{
    end: CommandParam<{
      regEx: string;
      sameDayAllowed: boolean;
    }>;
  }>;
  schedule: Command<{
    start: CommandParam<{
      regEx: string;
      sameDayAllowed: boolean;
    }>;
    end: CommandParam<{
      regEx: string;
      sameDayAllowed: boolean;
    }>;
  }>;
  unschedule: Command<{}>;
}

export interface EmsHocaFeaturesProperties {
  supported: ArrayValue<'onlineSolarIrradianceForecast'>;
  activated: ArrayValue<'onlineSolarIrradianceForecast'>;
}

// Burner Statistics
export interface BurnerStatisticsProperties {
  hours: NumberValue;
  starts: NumberValue;
  hoursLoadClassOne: NumberValue;
  hoursLoadClassTwo: NumberValue;
  hoursLoadClassThree: NumberValue;
  hoursLoadClassFour: NumberValue;
  hoursLoadClassFive: NumberValue;
}

// Burner Demand Temperature
export interface BurnerDemandTemperatureProperties {
  value: NumberValue;
  unit: StringValue;
}

// Ventilation
export interface VentilationProgramProperties {
  value?: StringValueGeneric<keyof typeof VentilationOperatingPrograms>;
}

export interface VentilationModeProperties {
  value?: StringValueGeneric<keyof typeof VentilationOperatingModesActivePrograms>;
}

export interface VitotrolRemoteControllerProperties {
  busType?: StringValue;
  deviceIndex?: RemoteControllerValue;
  deviceId?: StringValue;
}

export interface VentilationModeCommands {
  setMode: Command<{
    mode: CommandParam<{ enum: string[] }>;
  }>;
}

export interface VitotrolRemoteControllerCommands {
  setZigbeeController: Command<{ deviceId: CommandParam<{ enum: string[] }> }>;
  removeZigbeeController: Command<{}>;
}

export enum RoomsOperatingStateDemand {
  heating = 'heating',
  cooling = 'cooling',
  energySaving = 'energySaving',
}

export enum RoomsOperatingStateReason {
  // https://dc-wiki.viessmann.io/wiki/index.php/Production:_rooms.others.N.operating.state_reason define just one
  standby = 'standby',
  manualTillNextSchedule = 'manualTillNextSchedule',
  noDemand = 'noDemand',
  heatOnTime = 'heatOnTime',
  openWindow = 'openWindow',
  geoFencing = 'geoFencing',
  holidayMode = 'holidayMode',
  holidayAtHome = 'holidayAtHome',
  stayAtHome = 'stayAtHome',
  frostProtection = 'frostProtection',
  schedule = 'schedule',
}

export enum RoomsOperatingStateModifier {
  heatOnTime = 'heatOnTime',
  openWindow = 'openWindow',
  geoFencing = 'geoFencing',
  frostProtection = 'frostProtection',
  none = 'none',
}

export enum RoomsOperatingStateLevel {
  reduced = 'reduced',
  normal = 'normal',
  comfort = 'comfort',
  unknown = 'unknown',
}

export enum TemperatureLevelsPerceivedPrograms {
  reduced = 'reduced',
  normal = 'normal',
  comfort = 'comfort',
}

export enum VentilationOperatingModesActivePrograms {
  // https://dc-wiki.viessmann.io/wiki/index.php/Production:_ventilation.operating.modes.active_value define those 3 values but
  standby = 'standby',
  standard = 'standard',
  ventilation = 'ventilation',
  // below is taken from command, above is all values provided by DC, possible bug in wiki, we can set more than what is 'possible'
  // https://dc-wiki.viessmann.io/wiki/index.php/Production:_ventilation.operating.modes.active
  permanent = 'permanent',
  active = 'active',
  sensorDriven = 'sensorDriven',
  sensorOverride = 'sensorOverride',
  filterChange = 'filterChange',
  continuousSensorOverride = 'continuousSensorOverride',
}

export enum VentilationOperatingPrograms {
  levelOne = 'levelOne',
  levelTwo = 'levelTwo',
  levelThree = 'levelThree',
  levelFour = 'levelFour',
  standby = 'standby',
  unknown = 'unknown',
}

export interface VentilationPermanentProperties {
  active: BooleanValue;
  value?: StringValue;
}
export interface VentilationPermanentCommands {
  setLevel: Command<{
    level: CommandParam<{ enum: string[] }>;
  }>;
}

export interface VentilationBypassModeProperties {
  value?: StringValue;
  state?: StringValue;
  level?: StringValue;
}
export interface VentilationBypassModeCommands {
  setMode: Command<{
    mode: CommandParam<{ enum: string[] }>;
  }>;
}

export interface VentilationAutomatedModeLevelProperties {
  minimumLevel?: StringValue;
  maximumLevel?: StringValue;
}
export interface VentilationAutomatedModeLevelCommands {
  setLevels: Command<{
    minimumLevel: CommandParam<{ enum: string[] }>;
    maximumLevel: CommandParam<{ enum: string[] }>;
  }>;
}

export interface VentilationBypassConfigurationProperties {
  value?: StringValue;
}
export interface VentilationBypassConfigurationCommands {
  setValue: Command<{
    value: CommandParam<{ enum: string[] }>;
  }>;
}
export interface VentilationBypassPositionProperties {
  value?: NumberValue;
  unit?: StringValue;
}

export interface VentilationQuickSelectionsProperties {
  active: BooleanValue;
  defaultRuntime: MinutesValue;
  remainingRuntime: MinutesValue;
}

export interface VentilationFilterRuntime {
  operatingHours: NumberValue;
  remainingHours: NumberValue;
  overdueHours: NumberValue;
}

export interface VentilationQuickSelectionsCommands {
  activate: Command<{
    timeout: CommandParam<{ min: number; max: number; stepping: number }>;
  }>;
  deactivate: Command<{}>;
}

export interface VentilationProgramDetails {
  name: string;
  displayName?: string;
  css: string;
}

export interface NoiseReductionProperties {
  active: BooleanValue;
  changeableByEndCustomer: BooleanValue;
}

export interface NoiseReductionCommands {
  setChangeableByEndCustomer: Command<{
    changeable: CommandParam<{
      type: boolean;
      constraints: any;
      required: number;
    }>;
  }>;
}

export interface VentilationStateProperties {
  level: StringValueGeneric<keyof typeof VentilationOperatingPrograms>;
  demand: StringValueGeneric<keyof typeof VentulationOperationDemands>;
  reason: StringValueGeneric<keyof typeof VentilationOperatingReasons>;
}
// Heating Curve
export interface HeatingCurveProperties {
  shift: NumberValue;
  slope: NumberValue;
}

export interface HeatingCurveCommands {
  setCurve: Command<{
    slope: CommandParam<{
      min: number;
      max: number;
      stepping: number;
    }>;
    shift: CommandParam<{
      min: number;
      max: number;
      stepping: number;
    }>;
  }>;
}

// Call For Heat Demand
export interface CallForHeatDemandProperties {
  value: BooleanValue;
}

// Call For Heat Configuration Regulation
export interface HeatingConfigurationRegulationProperties {
  mode: StringValueGeneric<keyof typeof HeatingConfigurationRegulationMode>;
}

export enum HeatingConfigurationRegulationMode {
  Nothing = 'Nothing',
  ConstantControlled = 'ConstantControlled',
  DeltaTControlled = 'DeltaTControlled',
  RoomControlled = 'RoomControlled',
  WeatherByOutsideSensorControlled = 'WeatherByOutsideSensorControlled',
  WeatherByRemoteOutsideSensorControlled = 'WeatherByRemoteOutsideSensorControlled',
  WeatherByServiceControlled = 'WeatherByServiceControlled',
  WeatherByOutsideSensorAndRoomCorrectionControlled = 'WeatherByOutsideSensorAndRoomCorrectionControlled',
  WeatherByRemoteOutsideSensorAndRoomCorrectionControlled = 'WeatherByRemoteOutsideSensorAndRoomCorrectionControlled',
  WeatherByServiceAndRoomCorrectionControlled = 'WeatherByServiceAndRoomCorrectionControlled',
  ConstantControlledWithExternalThermostat = 'ConstantControlledWithExternalThermostat',
  WeatherByOutsideSensorWithExternalThermostat = 'WeatherByOutsideSensorWithExternalThermostat',
  WeatherByOutsideSensorRoomByRoom = 'WeatherByOutsideSensorRoomByRoom',
  BasicConstantFlowControl = 'BasicConstantFlowControl',
  ConstantFlowControlOpenTherm = 'ConstantFlowControlOpenTherm',
  DeltaTRoomOnOff = 'DeltaTRoomOnOff',
  DeltaTRoomModulation = 'DeltaTRoomModulation',
  DeltaTRoomWithWeatherForeCast = 'DeltaTRoomWithWeatherForeCast',
  error = 'error',
}

export interface HeatingPrimaryCircuitAirSourceProperties {
  mode: StringValueGeneric<keyof typeof HeatingPrimaryCircuitAirSourceMode>;
}

export enum HeatingPrimaryCircuitAirSourceMode {
  ambient = 'ambient',
  extract = 'extract',
  outdoor = 'outdoor',
  exhaust = 'exhaust',
  unknown = 'unknown',
}

// Space Climate Cooling
export interface SpaceClimateCoolingProperties {
  activationThreshold: NumberValue;
  deactivationThreshold: NumberValue;
}

export interface SpaceClimateCoolingCommands {
  setThresholds: Command<{
    activationThreshold: CommandParam<RangeConstraints>;
    deactivationThreshold: CommandParam<RangeConstraints>;
  }>;
}

export interface CirculationPumpProperties {
  status: StringValue;
}

export interface ChildLockProperties {
  status: StringValue;
}

export interface frostProtectionProperties {
  status: StringValueGeneric<keyof typeof OnOff>;
}

export interface offPeakSignalProperties {
  currentState: StringValueGeneric<keyof typeof OffPeakSignal>;
}

export interface heatingDhwPumpStatus {
  status: StringValueGeneric<keyof typeof OnOff>;
}

export interface InternalHydraulicPumpStatus {
  status: StringValueGeneric<keyof typeof OnOff>;
}

export interface HeatingBurnersNErrorStatusProperties {
  errorCode: NumberValue;
  status: StringValueGeneric<keyof typeof HeatingBurnersNErrorStatus>;
}

// Schedule
export interface ScheduleProperties {
  active: BooleanValue;
  entries: {
    type: DcTypes.Schedule;
    value: SchedulePropertiesValue;
  };
}

export interface SchedulePropertiesValue {
  mon: ScheduleEntryProperty[];
  tue: ScheduleEntryProperty[];
  wed: ScheduleEntryProperty[];
  thu: ScheduleEntryProperty[];
  fri: ScheduleEntryProperty[];
  sat: ScheduleEntryProperty[];
  sun: ScheduleEntryProperty[];
}

export interface ScheduleEntryProperty {
  start: string;
  end: string;
  position: number;
  mode?: string;
}

export interface ScheduleCommands {
  setSchedule: Command<{
    newSchedule: CommandParam<{
      modes: string[];
      maxEntries: number;
      resolution: number;
      defaultMode: string;
    }>;
  }>;
}

export interface DhwTemperatureCommands {
  setTargetTemperature: Command<{
    temperature: CommandParam<DhwRangeConstraints>;
  }>;
}

export interface OtherRoomsCommands {
  add: Command<{ name: CommandParam<RangeConstraints>; heatingCircuit: number }>;
}

export interface RoomsCommands {
  add: Command<{
    name: CommandParam<{
      minLength: number;
      maxLength: number;
      regEx: string;
    }>;
    type: CommandParam<RoomPropertiesType>;
  }>;
}

export interface OtherRoomProperties {
  active: BooleanValue;
  actors: ArrayValue<{ deviceId: string; heatingCircuit: number }>;
  heatingCircuit: NumberValue;
  name: StringValue;
}

export interface OtherRoomCommands {
  activate: Command<{}>;
  addActor: Command<{ actorDeviceId: RoomCommandParam }>;
  deactivate: Command<{}>;
  moveActor: Command<{ actorDeviceId: RoomCommandParam; newRoomId: RoomCommandParam }>;
  removeActor: Command<{ actorDeviceId: RoomCommandParam }>;
  setName: Command<{ name: CommandParam<RangeConstraints> }>;
}
// Room
export interface RoomProperties {
  name: StringValue;
  type: StringValueGeneric<RoomPropertiesType>;
  actors: ArrayValue<{ deviceId: string; heatingCircuit: number }>;
}

export type RoomPropertiesType =
  | 'bathroom'
  | 'bedroom'
  | 'hallway'
  | 'kitchen'
  | 'livingroom'
  | 'nursery'
  | 'office'
  | 'other'
  | 'toilet'
  | 'invalid';

export interface AddRoom {
  name: string;
  type: RoomPropertiesType;
}
export interface RoomCommands {
  addActor: Command<{
    actorDeviceId: RoomCommandParam;
    heatingCircuit: RoomCommandParam;
  }>;
  moveActor: Command<{
    actorDeviceId: RoomCommandParam;
    heatingCircuit: RoomCommandParam;
    newRoomId: RoomCommandParam;
  }>;
  remove: Command<{}>;
  removeActor: Command<{
    actorDeviceId: RoomCommandParam;
  }>;
  setName: Command<{
    name: CommandParam<RangeConstraints>;
  }>;
  setType: Command<{
    type: CommandParam<{
      enum: string[];
    }>;
  }>;
}

// Room Open Point Detection
export interface RoomOpenPointDetectionActivityProperties {
  value: PercentValue;
  unit: PercentUnit;
}

// Etrv Battery
export interface BatteryProperties {
  level: NumberValue;
}

export interface BatteryInformationFeatures {
  stateOfCharge: NumberValue;
  stateOfHealth: NumberValue;
  internalBatteryResistance: NumberValue;
  capacityOfEachModule: NumberValue;
  countCoulombCharge: NumberValue;
  countCoulombDischarge: NumberValue;
  energyOfModule: NumberValue;
  cycleDischarge: NumberValue;
  productionDate: NumberValue;
  overTemperatureWhileChargingTime: NumberValue;
  belowTemperatureWhileChargingTime: NumberValue;
  overTemperatureTime: NumberValue;
  belowTemperatureTime: NumberValue;
  deepDischargeTime: NumberValue;
  countEnergyDischarge: NumberValue;
}

export enum BatteryInformationFeaturesType {
  stateOfCharge = 'stateOfCharge',
  stateOfHealth = 'stateOfHealth',
  internalBatteryResistance = 'internalBatteryResistance',
  capacityOfEachModule = 'capacityOfEachModule',
  countCoulombCharge = 'countCoulombCharge',
  countCoulombDischarge = 'countCoulombDischarge',
  energyOfModule = 'energyOfModule',
  cycleDischarge = 'cycleDischarge',
  productionDate = 'productionDate',
  overTemperatureTime = 'overTemperatureTime',
  belowTemperatureTime = 'belowTemperatureTime',
  overTemperatureWhileChargingTime = 'overTemperatureWhileChargingTime',
  belowTemperatureWhileChargingTime = 'belowTemperatureWhileChargingTime',
  deepDischargeTime = 'deepDischargeTime',
  countEnergyDischarge = 'countEnergyDischarge',
}
// Etrv Signal
export interface SignalProperties {
  strength: NumberValue;
}

export interface GatewayConnectionType {
  mode: StringValueGeneric<keyof typeof GatewayConnectionMode>;
}

export interface EtrvMotorPositionProperties {
  position: NumberValue;
  unit: StringValue;
}

export interface RawConsumptionProperties {
  day: ArrayValue<number>;
  week: ArrayValue<number>;
  month: ArrayValue<number>;
  year: ArrayValue<number>;
  unit?: StringValue;
  dayValueReadAt?: StringValue;
  weekValueReadAt?: StringValue;
  monthValueReadAt?: StringValue;
  yearValueReadAt?: StringValue;
}

export interface ActiveHeatOnTime {
  value: boolean;
  type: string;
}

export interface DeviceConnectionStatus {
  status: {
    type: DcTypes.string;
    value: DeviceConnectionStatusValues;
  };
}

// source:
// https://dc-wiki.westeurope.cloudapp.azure.com/wiki/index.php/Production:_device.connection_status
export enum DeviceConnectionStatusValues {
  connected = 'connected',
  paired = 'paired',
  unknown = 'unknown',
}

// source:
// https://dc-wiki.westeurope.cloudapp.azure.com/wiki/index.php/Staging:_heating.circuits.N.zone.demand
export interface ZoneDemandProperties {
  present: BooleanValue;
}

export interface PhotovoltaicStatusProperties {
  status: StringValueGeneric<keyof typeof PhotovoltaicStatusStatus>;
}

export interface PhotovoltaicInsulationResistanceProperties {
  value: NumberValue;
  status: StringValue;
}

export enum PhotovoltaicInsulationResistanceStatus {
  valid = 'valid',
}

export enum PhotovoltaicStatusStatus {
  nothing,
  ready,
  production,
  unknown,
}

export interface PvSignalProperties {
  currentState: StringValueGeneric<keyof typeof pvSignal>;
}

export enum pvSignal {
  pvAvailable = 'pvAvailable',
  pvNotAvailable = 'pvNotAvailable',
}

export interface EmsKpiProperties {
  autarky: NumberValue;
  selfConsumption: NumberValue;
  calculationStatus: StringValueGeneric<keyof typeof CalculationStatusValues>;
}
export enum CalculationStatusValues {
  notInitialized = 'notInitialized',
  correct = 'correct',
  discarded = 'discarded',
  adjusted = 'adjusted',
}

export enum GatewayConnectionMode {
  ethernet = 'ethernet',
  wifi = 'wifi',
}

export interface CumulatedEnergyProperties {
  currentDay: NumberValue;
  currentWeek: NumberValue;
  currentMonth: NumberValue;
  currentYear: NumberValue;
  lifeCycle: NumberValue;
}

export interface SummaryConsumptionProperties {
  currentDay: NumberValue;
  currentMonth: NumberValue;
  currentYear: NumberValue;
  lastSevenDays: NumberValue;
}

export interface EssOperationStateProperties {
  value: StringValueGeneric<keyof typeof EssOperationStateValues>;
}

export enum EssOperationStateValues {
  off = 'off',
  initialization = 'initialization',
  ready = 'ready',
  precharge = 'precharge',
  charge = 'charge',
  discharge = 'discharge',
  safetyCheck = 'safetyCheck',
  error = 'error',
  idle = 'idle',
  standby = 'standby',
  safetyCheckRequest = 'safetyCheckRequest',
  shutDown = 'shutDown',
  unknown = 'unknown',
}

export interface EssBatteryStateProperties {
  basic: {
    value: EssStateBasicValues[];
    type: DcTypes.array;
  };
  exception: {
    value: EssStateExceptionValues[];
    type: DcTypes.array;
  };
}

export enum EssStateExceptionValues {
  batteryError = 'batteryError',
  balancingFewDays = 'balancingFewDays',
  balancingFewHours = 'balancingFewHours',
  balancingOneWeeks = 'balancingOneWeeks',
  balancingTwoWeeks = 'balancingTwoWeeks',
  batterySleepMode = 'batterySleepMode',
  batteryCscPowerDown = 'batteryCscPowerDown',
  islandModeBatteryLow = 'islandModeBatteryLow',
  ambientTemperatureTooHigh = 'ambientTemperatureTooHigh',
  ambientTemperatureTooLow = 'ambientTemperatureTooLow',
  initialBatteryAdjustment = 'initialBatteryAdjustment',
  systemStateSelfTestActive = 'systemStateSelfTestActive',
  batteryDeepDischargeRecharge = 'batteryDeepDischargeRecharge',
  systemStateIsloandModeActive = 'systemStateIsloandModeActive',
  systemstateUpdateInProgress = 'systemstateUpdateInProgress',
  batteryModuleExchangeAssistant = 'batteryModuleExchangeAssistant',
  batteryTemperatureDeratingActive = 'batteryTemperatureDeratingActive',
  islandModeRechargeToBackupStateOfCharge = 'islandModeRechargeToBackupStateOfCharge',
  systemStateIslandModeActive = 'systemStateIslandModeActive',
  noException = 'noException',
}

export enum EssStateBasicValues {
  initializing = 'initializing',
  charge = 'charge',
  discharge = 'discharge',
  lifetimeOptimization = 'lifetimeOptimization',
  maintenance = 'maintenance',
  ready = 'ready',
  systemStateStandby = 'systemStateStandby',
  systemStateError = 'systemStateError',
}

export interface EssPhotovoltaicStateProperties {
  basic: {
    value: EssPhotovoltaicStateBasicValues[];
    type: DcTypes.array;
  };
  exception: {
    value: EssPhotovoltaicStateExceptionValues[];
    type: DcTypes.array;
  };
}

export enum EssPhotovoltaicStateExceptionValues {
  photovoltaicError = 'photovoltaicError',
  feedInLimitationActive = 'feedInLimitationActive',
  systemStateIslandModeActive = 'systemStateIslandModeActive',
  systemStateSelfTestActive = 'systemStateSelfTestActive',
  systemStateUpdateInProgress = 'systemStateUpdateInProgress',
  limitEnergyManagementActive = 'limitEnergyManagementActive',
  photovoltaicTemperatureDeratingPhotovoltaicActive = 'photovoltaicTemperatureDeratingPhotovoltaicActive',
  noException = 'noException',
}

export enum EssPhotovoltaicStateBasicValues {
  initializing = 'initializing',
  charge = 'charge',
  discharge = 'discharge',
  lifetimeOptimization = 'lifetimeOptimization',
  maintenance = 'maintenance',
  ready = 'ready',
  systemStateStandby = 'systemStateStandby',
  systemStateError = 'systemStateError',
  producing = 'producing',
}

// Ess Configurations
export interface EssConfigurationProperties {
  supported: ArrayValue<string>;
  activated: ArrayValue<string>;
}

export interface EmsPowerFlowsProperties {
  status: StringValueGeneric<keyof typeof CalculationStatusValues>;
  productionToConsumption: NumberValue;
  productionToStorage: NumberValue;
  productionToGrid: NumberValue;
  gridToConsumption: NumberValue;
  gridToStorage: NumberValue;
  storageToConsumption: NumberValue;
  storageToStorage: NumberValue;
  storageToGrid: NumberValue;
}

export interface EssInverterPowerProperties {
  activePower: NumberValue;
  reactivePower: NumberValue;
}

// Cascades
export interface CascadeMembersProperties {
  enabled: {
    value: number[];
    type: DcTypes.array;
  };
}

export interface CascadeSequenceProperties {
  value: ArrayValue<number>;
}

export interface CascadeSequenceStrategyProperties {
  burnerRuntime: BooleanValue;
  fixedFirst: BooleanValue;
  fixedLast: BooleanValue;
  fixedMode: BooleanValue;
}

export interface CascadeSequenceStrategyCommands {
  activateBurnerRuntime: Command<{ burnerRuntime: BurnerRuntimeSequenceStrategyCommandParam }>;
  activateFixedFirst: Command<{ deviceName: FixedSequenceStrategyCommandParam }>;
  activateFixedLast: Command<{ deviceName: FixedSequenceStrategyCommandParam }>;
  activateFixedMode: Command<{}>;
  deactivateBurnerRuntime: Command<{}>;
  deactivateFixedFirst: Command<{}>;
  deactivateFixedLast: Command<{}>;
  deactivateFixedMode: Command<{}>;
}

export interface CascadeSquenceValuesProperties {
  active: BooleanValue;
  nodeId: StringValue;
  name: StringValue;
  type: StringValue;
}

export interface CascadeSquenceValuesCommands {
  setName: Command<{ name: CommandParam<RangeConstraints> }>;
}

export interface DcSensorValueProperties {
  status: StringValueGeneric<keyof typeof StatusValueDcFeature>;
  value: NumberValue;
}

export interface LagDeviceModulationProperties {
  value: PercentValue;
}

export interface DeviceMessageErrorsRawEntries {
  timestamp: string;
  errorCode: string;
  accessLevel: string;
  priority: string;
}

export interface DeviceMessageErrorsRaw {
  entries: DeviceMessageErrorsRawEntries[];
}

export enum EmsPowerBalanceTypes {
  Accumulator = 'Accumulator',
  Heatpump = 'Heatpump',
  Household = 'Household',
  Grid = 'Grid',
  Photovoltaic = 'Photovoltaic',
  PhotovoltaicsHeater = 'PhotovoltaicsHeater',
  test = 'test',
}

export interface EmsPowerBalanceProperty {
  type: EmsPowerBalanceTypes;
  value: number;
  unit: string;
}

export interface EmsPowerBalanceProperties {
  calculationStatus: StringValueGeneric<'notInitialized' | 'correct' | 'discarded' | 'adjusted'>;
  received: {
    value: EmsPowerBalanceProperty[];
  };
  delivered: {
    value: EmsPowerBalanceProperty[];
  };
  totalConsumption: NumberValue;
  totalProduction: NumberValue;
}

export interface EmsInternalComponentProperties {
  value: ArrayValue<string>;
}

export interface BufferTemperatureMainProperties {
  value: NumberValue;
  status: StringValueGeneric<keyof typeof StatusValueDcFeature>;
}

export interface HeatingProperties {
  value: NumberValue;
  unit: StringValue;
  status: StringValueGeneric<keyof typeof DeviceConnectionStatusValues>;
}

export interface ComfortEnsuringProperties {
  activated: BooleanValue;
  useApproved: BooleanValue;
}
export interface ComfortEnsuringCommands {
  acknowledgeActivation: Command<{}>;
  activateManually: Command<{}>;
  deactivateManually: Command<{}>;
  grant: Command<{}>;
  revoke: Command<{}>;
}

export interface FlowLimitTemperatureProperties {
  min: NumberValue;
  minUnit: StringValue;
  max: NumberValue;
  maxUnit: StringValue;
}

export interface FlowLimitTemperatureCommands {
  setMin: Command<{ temperature: CommandParam<RangeConstraints> }>;
  setMax: Command<{ temperature: CommandParam<RangeConstraints> }>;
  setLevels: Command<{
    minTemperature: CommandParam<RangeConstraints>;
    maxTemperature: CommandParam<RangeConstraints>;
  }>;
}

export interface CommonFlowTemperature {
  value: TemperatureValue;
}

export interface HeatingCascadeTemperatureCommonSupplyCommands {
  setValue: Command<{ value: CommandParam<RangeConstraints> }>;
}

// Battery
export interface BatteryInternalComponentsProperties {
  vinList: ArrayValue<string>;
}

// Solar
export interface SolarStatusProperties {
  status: StringValueGeneric<keyof typeof OnOff>;
}

export interface CirculationPumpStatusProperties {
  status: StringValueGeneric<keyof typeof OnOff>;
}

// SRC - Heat up speed
export interface HeatUpSpeedProperties {
  value: StringValue;
}

export enum HeatUpSpeedValues {
  verySlow = 'verySlow',
  slow = 'slow',
  moderate = 'moderate',
  normal = 'normal',
  fast = 'fast',
  veryFast = 'veryFast',
}

export interface HeatUpSpeedCommands {
  setValue: Command<HeatUpSpeedCommandParam>;
}

export interface HeatUpSpeedCommandParam {
  value: {
    type: 'string';
    required: boolean;
    constraints: {
      enum: HeatUpSpeedValues;
    };
    description: string;
  };
}

export interface HeatingCoolingCircuitRemoteDefrostCommands {
  setActive: Command<{
    active: CommandParam<{}>;
  }>;
}

// Ecowater
export interface SaltLevelProperties {
  remaining: NumberValue;
}

export interface LeakDetectionProperties {
  [FeaturePropertyKey.maxFlow]: NumberValue;
  [FeaturePropertyKey.maxDuration]: NumberValue;
}

export interface LeakDetectionCommands {
  setMaxFlow: Command<{
    maxFlow: CommandParam<{
      min: number;
      max: number;
      stepping: number;
    }>;
  }>;
  setMaxDuration: Command<{
    maxDuration: CommandParam<{
      min: number;
      max: number;
      stepping: number;
    }>;
  }>;
}

// Ecotronic
export interface DeviceFuelMinimalCommands {
  setMinimalFilling: Command<{
    minimalFilling: CommandParam<{
      min: number;
      max: number;
      stepping: number;
    }>;
  }>;
}

export interface DeviceFuelCurrentCommands {
  adjustTank: Command<{
    adjustWeight: CommandParam<{
      min: number;
      max: number;
      stepping: number;
    }>;
  }>;
}

export interface ServiceBurnerBasedCommands {
  setHourInterval: Command<{
    hour: CommandParam<{
      min: number;
      max: number;
      stepping: number;
    }>;
  }>;
}

export interface HeatingServiceBurnerBasedProperties {
  serviceDue: BooleanValue;
  serviceIntervalBurnerHours: NumberValue;
  activeBurnerHoursSinceLastService: NumberValue;
  lastService: StringValue; // this is iso string date
  burnerHoursUntilNextService: NumberValue;
}

export interface ServiceTimeBasedProperties {
  serviceDue: BooleanValue;
  serviceIntervalMonths: NumberValue;
  activeMonthSinceLastService: NumberValue;
  lastService: StringValue; // this is iso string date
  nextService: StringValue; // this is not ISO string date, just yyyy-MM-dd
}

export interface ServiceTimeBasedCommands {
  confirmService: Command<{}>;
}

// Heat pump E3 Smart Grid
export enum HeatingDeviceSmartGridEnum {
  off = 'off',
  normalOperation = 'normalOperation',
  recommendedOperation = 'recommendedOperation',
  forcedOperation = 'forcedOperation',
  unknown = 'unknown',
}

export interface SensorValue {
  status: StringValueGeneric<StatusValueDcFeature>;
  value: NumberValue;
}

// DHW
export interface DhwHotWaterStorageTemperatureBottom {
  status: StringValueGeneric<keyof typeof StatusValueDcFeature>;
  value?: NumberValue;
}

export interface DhwHotWaterStorageTemperatureTop {
  status: StringValueGeneric<keyof typeof StatusValueDcFeature>;
  value?: NumberValue;
}

export interface DhwHotWaterStorageTemperature {
  status: StringValueGeneric<keyof typeof StatusValueDcFeature>;
  value?: NumberValue;
}

export interface HeatingDhwProperties {
  active: BooleanValue;
  status: StringValue;
}

export interface HeatingConfigurationDhwSensorsEcoModeLeadingProperties {
  value: StringValueGeneric<keyof typeof HeatingConfigurationDhqSensorsEcoModeLeadingValue>;
}

export enum HeatingConfigurationDhqSensorsEcoModeLeadingValue {
  top = 'top',
  mid = 'mid',
  // unknown = 'unknown', - not used
}
export interface HeatingConfigurationDhwSensorsEcoModeLeadingCommands {
  setValue: Command<{
    value: CommandParam<{
      enum: Array<keyof typeof HeatingConfigurationDhqSensorsEcoModeLeadingValue>;
    }>;
  }>;
}

export interface HeatingCoolingCircuitRemoteDefrostProperties {
  active: BooleanValue;
  status: StringValue;
}

export interface HeatingCompressorsProperties {
  active: BooleanValue;
  phase: StringValueGeneric<keyof typeof HeatingCompressorsPhaseValues>;
}

export enum HeatingCompressorsPhaseValues {
  preparing = 'preparing',
  heating = 'heating',
  pause = 'pause',
  cooling = 'cooling',
  preparingDefrost = 'preparing-defrost',
  defrost = 'defrost',
  passiveDefrost = 'passive-defrost',
  off = 'off',
  notReady = 'not-ready',
  ready = 'ready',
}

// Heating Sensors
export interface VolumetricFlowReturn {
  status: StringValue;
  value?: NumberValue;
}

export interface FuelCellOperatingPhase {
  value: StringValueGeneric<keyof typeof HeatingFuelCellOperatingPhase>;
}

export enum HeatingFuelCellOperatingPhase {
  standby = 'standby',
  startup = 'startup',
  generation = 'generation',
  stop = 'stop',
  maintenance = 'maintenance',
}

export interface FuelCellOperatingModesActiveProperties {
  value: StringValueGeneric<keyof typeof HeatingFuelCellOperatingModesActive>;
}

export enum HeatingFuelCellOperatingModesActive {
  standby = 'standby',
  maintenance = 'maintenance',
  heatControlled = 'heatControlled',
  ecological = 'ecological',
  economical = 'economical',
  energyControlled = 'energyControlled',
  chimneySweeperMode = 'chimneySweeperMode',
  party = 'party',
  holidayWithFuelCellOn = 'holidayWithFuelCellOn',
  holidayWithFuelCellOff = 'holidayWithFuelCellOff',
  unknown = 'unknown',
}

export interface FuelCellOperatingModesActiveCommands {
  setMode: Command<{
    mode: CommandParam<{ enum: keyof typeof HeatingFuelCellOperatingModesActive }>;
  }>;
}

export interface HeatingDhwOperatingModesActiveProperties {
  value: StringValueGeneric<keyof typeof HeatingDhwOperatingModesActive>;
}

export enum HeatingDhwOperatingModesActive {
  balanced = 'balanced',
  comfort = 'comfort',
  eco = 'eco',
  off = 'off',
}

export interface HeatingDhwOperatingModesActiveCommands {
  setMode: Command<{
    mode: CommandParam<{ enum: keyof typeof HeatingDhwOperatingModesActive }>;
  }>;
}

export interface FuelCellStatisticsProperties {
  operationHours: NumberValue;
  productionHours: NumberValue;
  productionStarts: NumberValue;
}

export interface HeatPumpSmartGridProperties {
  requestedState: StringValueGeneric<keyof typeof HeatingDeviceSmartGridEnum>;
  actualState: StringValueGeneric<keyof typeof HeatingDeviceSmartGridEnum>;
}
export interface HeatPumpSmartGridCommands {
  requestSmartGridState: Command<{
    id: CommandParam<{ enum: keyof typeof HeatingDeviceSmartGridEnum }>;
  }>;
}

export enum HeatingZoneOperatingPrograms {
  NoDemand = 'noDemand',
  TTContact = 'ttContact',
  Standby = 'standby',
  ReducedEnergySaving = 'reducedEnergySaving',
}

export enum HeatingZoneReducedEnergySavingReason {
  NoDemand = 'noDemand',
}

export interface PropertiesStringValueGeneric<T> {
  value: StringValueGeneric<T>;
}

export enum WallboxSubstatuses {
  overloadProtectionActive = 'overloadProtectionActive',
  pvExcessNotSufficient = 'pvExcessNotSufficient',
  chargingWithReducedPower = 'chargingWithReducedPower',
}
export interface WallboxSubstatus {
  substatus: StringValueGeneric<WallboxSubstatuses>;
}

export enum WallboxStatuses {
  connected = 'connected',
  charging = 'charging',
  disconnected = 'disconnected',
  plugged = 'plugged',
  unplugged = 'unplugged',
}

export interface WallboxStatus {
  status: StringValueGeneric<WallboxStatuses>;
}

export interface WallboxSessionCharging {
  energy: NumberValue;
  time: NumberValue;
}

export interface SetNumberValueCommands {
  setValue: Command<NumberValueRangeParams>;
}

export interface NumberValueRangeParams {
  value: {
    type: 'number';
    required: boolean;
    description: string;
    constraints: RangeConstraints;
  };
}

export interface OperatingStateProperties {
  level: StringValue;
  demand: StringValue;
  reason: StringValue;
  modifier: StringValue;
}

export interface OperatingStatePropertiesGeneric<T> {
  level: StringValueGeneric<T>;
  demand: StringValue;
  reason: StringValue;
  modifier: StringValue;
}

export interface RoomOperatingStateProperties
  extends OperatingStatePropertiesGeneric<SRCProgramsActiveValue | SRCLegacyProgramsActiveValue> {}

export enum SRCLegacyProgramsActiveValue {
  normal = 'normal',
  reduced = 'reduced',
  comfort = 'comfort',
  standby = 'standby',
  manual = 'manual',
  manualTillNextSchedule = 'manualTillNextSchedule',
}

export enum SRCProgramsActiveValue {
  reducedEnergySaving = 'reducedEnergySaving',
  reducedHeating = 'reducedHeating',
  reducedCooling = 'reducedCooling',
  normalEnergySaving = 'normalEnergySaving',
  normalHeating = 'normalHeating',
  normalCooling = 'normalCooling',
  comfortEnergySaving = 'comfortEnergySaving',
  comfortHeating = 'comfortHeating',
  comfortCooling = 'comfortCooling',
  manualEnergySaving = 'manualEnergySaving',
  manualHeating = 'manualHeating',
  manualCooling = 'manualCooling',
  manualTillNextScheduleEnergySaving = 'manualTillNextScheduleEnergySaving',
  manualTillNextScheduleHeating = 'manualTillNextScheduleHeating',
  manualTillNextScheduleCooling = 'manualTillNextScheduleCooling',
  standby = 'standby',
}

export interface DeviceSensorsCo2Properties {
  status: StringValueGeneric<keyof typeof DeviceConnectionStatusValues>;
  value: PartsPerMillionValue;
  unit: PartsPerMillionUnit;
}

export interface RoomSensorsCo2Properties {
  value: PartsPerMillionValue;
  status: StringValue;
}

export interface RoomCo2Properties {
  concentration: NumberValue;
}

export interface RoomCo2Commands {
  setConcentration: Command<{
    concentration: CommandParam<RangeConstraints>;
  }>;
}

export interface CondensationRiskProperties {
  value: BooleanValue;
}

export interface FhtDemandRelayModeCommand {
  setValue: Command<{
    value: CommandParam<{
      enum: FhtDemandRelayModeValues;
    }>;
  }>;
}

export enum FhtDemandRelayModeValues {
  automatic = 'automatic',
  off = 'off',
}

export enum HeatingDhwRustSurveillanceAnodeStatusValues {
  connected = 'connected',
  notConnected = 'notConnected',
  error = 'error',
}

export interface HeatingDhwRustSurveillanceAnodeProperties {
  value: NumberValue;
  status: StringValueGeneric<keyof typeof HeatingDhwRustSurveillanceAnodeStatusValues>;
}

export interface HeatingDhwScheduleModeProperties {
  value: StringValueGeneric<keyof typeof HeatingDhwScheduleModeValue>;
}
export interface HeatingDhwScheduleModeCommands {
  setMode: Command<{
    mode: CommandParam<{ enum: Array<keyof typeof HeatingDhwScheduleModeValue> }>;
  }>;
}

export enum HeatingDhwScheduleModeValue {
  automatic = 'automatic',
  individual = 'individual',
}

export enum HeatingBurnersNErrorStatus {
  ok = 'ok',
  error = 'error',
}

export interface CircuitProperty {
  active: BooleanValue;
  name: StringValue;
  type: StringValue;
}

export interface HeatingDeviceTimeCommands {
  setTime: Command<{
    value: CommandParam<{ regEx: string }>;
  }>;
}

export interface VentilationFanProperties {
  current: NumberValue;
  target: NumberValue;
  status: StringValueGeneric<keyof typeof StatusValueDcFeature>;
}

export enum OnOff {
  on = 'on',
  off = 'off',
}

export enum NormallyOpenedClosed {
  normallyClosed = 'normallyClosed',
  normallyOpened = 'normallyOpened',
}

export enum OffPeakSignal {
  economyTariff = 'economyTariff',
  premiumTariff = 'premiumTariff',
}

export interface FhtValveProperties {
  type: StringValueGeneric<keyof typeof NormallyOpenedClosed>;
}

export interface FhtValveCommands {
  setType: Command<{
    type: CommandParam<{ enum: Array<keyof typeof NormallyOpenedClosed> }>;
  }>;
}

export interface DhwTemperatureHysteresisProperties {
  value: NumberValue;
  switchOnValue: NumberValue;
  switchOffValue: NumberValue;
}

export interface DhwTemperatureHysteresisCommands {
  setHysteresisSwitchOnValue: Command<{
    hysteresis: CommandParam<{ enum: string[] }>;
  }>;
  setHysteresisSwitchOffValue: Command<{
    hysteresis: CommandParam<{ enum: string[] }>;
  }>;
}

export interface CoolingTemperatureProgram {
  value: BooleanValue;
  temperature: NumberValue;
  demand: StringValue;
}

export interface OperatingProgramsReducedEnergySavingProperties {
  active: BooleanValue;
  reason: StringValue;
  demand: StringValue;
}

export interface CoolingTemperatureProgramCommands {
  setTemperature: Command<{}>;
  activate: Command<{}>;
  deactivate: Command<{}>;
}

export interface DhwHygieneProperties {
  active: BooleanValue;
  enabled: BooleanValue;
}

export interface DhwHygieneCommands {
  enable: Command<{}>;
  disable: Command<{}>;
  activate: Command<{}>;
}

export interface DhwHygieneTriggerProperties {
  weekdays: ArrayValue<string>;
  startHour: NumberValue;
  startMinute: NumberValue;
}

export interface DhwHygieneTriggerCommands {
  triggerDaily: Command<{
    startHour: {
      type: string;
      constraints: RangeConstraints;
      required: boolean;
    };
    startMinute: {
      type: string;
      constraints: RangeConstraints;
      required: boolean;
    };
  }>;
  triggerOncePerWeek: Command<{
    startHour: {
      type: string;
      constraints: RangeConstraints;
      required: boolean;
    };
    startMinute: {
      type: string;
      constraints: RangeConstraints;
      required: boolean;
    };
    weekday: {
      type: string;
      constraints: {
        enum: string[];
      };
      required: boolean;
    };
  }>;
  activate: Command<{}>;
}

export interface VentilationPreHeaterStatisticsProperties {
  hours: NumberValue;
  starts: NumberValue;
}
export interface HeatingBurnersStatisticsProperties {
  hours: NumberValue;
  starts: NumberValue;
}

export interface VentilationHeatExchangerFrostprotectionProperties {
  status: StringValueGeneric<keyof typeof OnOff>;
}

export interface VentilationLevelsProperties {
  volumeFlow: CubicMeterPerHourValue;
}

export interface HeatingSolarStatisticsProperties {
  hours: NumberValue;
}

export interface DeviceBusTopologyProperties {
  value: ArrayValue<DeviceBusTopology>;
}

export interface DeviceBusTopology {
  busAddress: number;
  busType: string;
  deviceObjectProperty: string;
  deviceFunction: string;
  softwareVersion: string;
  hardwareVersion: string;
  etn: string;
}

export interface VentillationBypassTemperatureCommands {
  setTemperature: Command<{
    temperature: {
      type: string;
      constraints: RangeConstraints;
      required: boolean;
      description: string;
    };
  }>;
}

export interface AlternativeBivalentTemperatureCommands {
  setTemperature: Command<{
    temperature: {
      type: string;
      constraints: RangeConstraints;
      required: boolean;
      description: string;
    };
  }>;
}

export interface SpaceFrostProtectionCommands {
  setTemperature: Command<{
    temperature: {
      type: string;
      constraints: RangeConstraints;
      required: boolean;
      description: string;
    };
  }>;
}
export enum DhwConfigurationPumpsCirculationModes {
  timeScheduled = 'timeScheduled',
  cycledFix = 'cycledFix',
  externalDemand = 'externalDemand',
  cycledConfigured = 'cycledConfigured',
  permanent = 'permanent',
}

export interface DhwConfigurationPumpsCirculationParams {
  mode: CommandParam<{
    enum: [
      DhwConfigurationPumpsCirculationModes.timeScheduled,
      DhwConfigurationPumpsCirculationModes.cycledFix,
      DhwConfigurationPumpsCirculationModes.externalDemand,
      DhwConfigurationPumpsCirculationModes.cycledConfigured,
      DhwConfigurationPumpsCirculationModes.permanent
    ];
  }>;
}
export interface DhwConfigurationPumpsCirculationMode {
  setMode: Command<DhwConfigurationPumpsCirculationParams>;
}

export interface DhwConfigurationPumpsCirculationTriggerCycleCommands {
  setValue: Command<DhwConfigurationPumpsCirculationTriggerCycleParams>;
}
export interface DhwConfigurationPumpsCirculationTriggerCycleParams {
  value: CommandParam<{
    min: number;
    max: number;
    stepping: number;
  }>;
}

export enum DhwConfigurationPumpsCirculationCycleConfigurationModes {
  oneCycle = 'oneCycle',
  twoCycles = 'twoCycles',
  threeCycles = 'threeCycles',
  fourCycles = 'fourCycles',
  fiveCycles = 'fiveCycles',
  sixCycles = 'sixCycles',
}

export interface DhwConfigurationPumpsCirculationCycleConfigurationParams {
  mode: CommandParam<{
    enum: [
      DhwConfigurationPumpsCirculationCycleConfigurationModes.oneCycle,
      DhwConfigurationPumpsCirculationCycleConfigurationModes.twoCycles,
      DhwConfigurationPumpsCirculationCycleConfigurationModes.threeCycles,
      DhwConfigurationPumpsCirculationCycleConfigurationModes.fourCycles,
      DhwConfigurationPumpsCirculationCycleConfigurationModes.fiveCycles,
      DhwConfigurationPumpsCirculationCycleConfigurationModes.sixCycles
    ];
  }>;
}
export interface DhwConfigurationPumpsCirculationCycleConfigurationMode {
  setMode: Command<DhwConfigurationPumpsCirculationCycleConfigurationParams>;
}

export interface ZigbeeCoordinatorCommands {
  addDevice: Command<{
    id: CommandParam<{
      minLength: number;
      maxLength: number;
    }>;
    ic: CommandParam<{
      minLength: number;
      maxLength: number;
    }>;
    type: CommandParam<{
      enum: keyof typeof ZigbeeCoordinatorAddDeviceType;
    }>;
  }>;
  removeDevice: Command<{
    id: CommandParam<{
      minLength: number;
      maxLength: number;
    }>;
  }>;
}

export enum ZigbeeCoordinatorAddDeviceType {
  cs = 'cs',
  fht = 'fht',
  repeater = 'repeater',
  trv = 'trv',
  remoteController = 'remoteController',
  airQualitySensor = 'airQualitySensor',
}

export interface OperationalDataApiModel {
  listAllGatewayFeatures(installationId: number, gatewaySerial: string): Observable<DcFeatures>;
}
//There is some case in VentilationOperatingReason does not have the translation yet. Created 2 enum just in case it is needed
//in the future
export enum VentilationOperatingReasonsForTranslation {
  silent = 'silent',
  standby = 'standby',
  forcedLevelFour = 'forcedLevelFour',
  holiday = 'holiday',
  eco = 'eco',
  comfort = 'comfort',
  filterChange = 'filterChange',
}

export enum VentilationOperatingReasons {
  silent = 'silent',
  permanent = 'permanent',
  sensorDriven = 'sensorDriven',
  sensorOverride = 'sensorOverride',
  standard = 'standard',
  schedule = 'schedule',
  standby = 'standby',
  filterChange = 'filterChange',
  forcedLevelFour = 'forcedLevelFour',
  holiday = 'holiday',
  eco = 'eco',
  comfort = 'comfort',
}

export enum VentilationModesBypassConfiguration {
  smooth = 'smooth',
  dynamic = 'dynamic',
}

export enum VentulationOperationDemands {
  ventilation = 'ventilation',
  unknown = 'unknown',
}
export enum VentilationModes {
  Standby = 'standby',
  Permanent = 'permanent',
  Ventilation = 'ventilation',
  SensorOverride = 'sensorOverride',
  SensorDriven = 'sensorDriven',
  Off = 'off',
  AutomaticSmoothRegulation = 'automaticSmoothRegulation',
  AutomaticDynamicRegulation = 'automaticDynamicRegulation',
  Standard = 'standard',
  ContinuousSensorOverride = 'continuousSensorOverride',
  Closed = 'closed',
  Open = 'open',
  Automatic = 'automatic',
  filterChange = 'filterChange',
}
export interface ConsolidatorDevice {
  id: string;
  status: string;
  role: string;
  value: number;
  unit: string;
  memberId: number;
  properties?: {
    total: {
      type: string;
      unit: string;
      value: number;
    };
  };
}
export interface ConsolidatedFeature {
  total: NumberValue;
  devices: {
    type: DcTypes.string;
    value: ConsolidatorDevice[];
  };
}

export interface PvHeaterPowerConsumptionCurrentProperties {
  value: NumberValue;
  status: StringValueGeneric<keyof typeof PvHeaterPowerConsumptionCurrentStatus>;
}

export enum PvHeaterPowerConsumptionCurrentStatus {
  active = 'active',
  standby = 'standby',
  error = 'error',
}

export const VitotrolName = {
  Vitotrol_300E: 'Vitotrol 300-E',
  Vitotrol_100E: 'Vitotrol 100-E',
};

export const BetaFeatures = ['ventilation.filter.pollution.blocked'];

export const PvStringIdLetter = {
  0: 'B',
  1: 'C',
  2: 'A',
};

export const VentilationAutomatedModeLevel = {
  levelOne: 1,
  levelTwo: 2,
  levelThree: 3,
  levelFour: 4,
};

export interface ClimateSensorAndCo2DisconnectStatus {
  allDevicesDisconnected: boolean;
  isAnyCsDisconnected: boolean;
  isAnyCo2Disconnected: boolean;
}

export interface HeatingBoilerPumpInternalProperties {
  status: StringValue;
}

export interface HeatingSecondaryCircuitValvesFourThreeWayProperties {
  current: NumberValue;
  target: NumberValue;
}
export interface HeatingValvuesFourThreeWayPositionProperties {
  value: StringValue;
}

export interface HeatingConfigurationInternalPumps {
  value: NumberValue;
}

export interface FanRingCondensatePan {
  fanRing: Feature<IsActiveProperty, ActivateDeactivateCommands>;
  condensatePan: Feature<IsActiveProperty, ActivateDeactivateCommands>;
}

export interface VentilationSensorsActuatorSelftestProperties {
  enabled: BooleanValue;
  status: StringValue;
}

export interface VentilationFilterInformationProperties {
  filterRuntimeResetValue: NumberValue;
  filterOneEan: StringValue;
  filterTwoEan: StringValue;
}

export interface VentilationLockExternalProperties {
  airPressureMonitor: BooleanValue;
  fireProtectionFlap: BooleanValue;
}

export interface FrostProtectionProperties {
  type: StringValueGeneric<keyof typeof FrostProtectionType>;
}

export enum FrostProtectionType {
  undefined = 'undefined',
  enthalpy = 'enthalpy',
  nonEnthalpy = 'nonEnthalpy',
}

export enum VentilationHeatExchangerFrostProtectionType {
  disbalance = 'disbalance',
  intermittent = 'intermittent',
  preheater = 'preheater',
  preheaterEcoMode = 'preheaterEcoMode',
}

export interface FrostProtectionCommands {
  setType: Command<{
    type: CommandParam<{ enum: string[] }>;
  }>;
}

export interface DeviceConfigProperty {
  circuitsEnabled: ArrayValue<string>;
}

export interface StandAloneProperty {
  active: BooleanValue;
}

export interface DiverterValveProperty {
  position: {
    type: DcTypes.string;
    value: DiverterValve;
  };
}

export interface TemperatureSeparatorProperty {
  status: {
    type: DcTypes.string;
    value: DcTypes.string;
  };
  value?: {
    type: DcTypes.string;
    value: DcTypes.number;
    unit: DcTypes.string;
  };
}
export const DeviceIconName = {
  vitotrol300E: 'remote-controller-vitotrol-300E',
};

export interface TariffBasedFinancialOptimizationStateProperty {
  active: BooleanValue;
  featureSupported: BooleanValue;
  tariffsActive: BooleanValue;
  subscriptionActive: BooleanValue;
}
export interface TypeBooleanCommandParam {
  type: boolean;
  required: boolean;
}
export interface TariffBasedFinancialOptimizationStateCommands {
  setActive: Command<{
    active: TypeBooleanCommandParam;
  }>;
}
export interface PowerLimitationTooltip {
  label: string;
  isConsumption: boolean;
  value: number;
  unit: string;
}

export interface PowerLimitationTooltipValues {
  consumptionLimitationTooltip: string;
  productionLimitationTooltip: string;
}

export class LagDeviceBasicModel {
  id: string;
  name: string;
}

export interface HeatingConfigurationSecondaryHeatGeneratorModeOnErrorCommand {
  setMode: Command<{
    mode: CommandParam<{
      enum: Array<keyof typeof HeatingConfigurationSecondaryHeatGeneratorModeOnErrorValue>;
    }>;
  }>;
}
export interface HeatingConfigurationSecondaryHeatGeneratorModeOnErrorProperties {
  mode: StringValueGeneric<keyof typeof HeatingConfigurationSecondaryHeatGeneratorModeOnErrorValue>;
}

export interface HeatingSecondaryHeatGeneratorRuntimeProperties {
  minimal: NumberValue;
  break: NumberValue;
  delay: NumberValue;
}

export interface HeatingSecondaryHeatGeneratorRuntimeCommands {
  setMinimalRuntime: Command<{
    minimalRuntime: CommandParam<RangeConstraints>;
  }>;
  setBreakTime: Command<{
    breakTime: CommandParam<RangeConstraints>;
  }>;
  setDelayTime: Command<{
    delayTime: CommandParam<RangeConstraints>;
  }>;
}

export interface SecondaryHeatGeneratorConfigurationTimeIntegralThresholdCommand {
  setValue: Command<{
    turnOn: CommandParam<RangeConstraints>;
  }>;
}

export interface SecondaryHeatGeneratorConfigurationOffsetCommand {
  setValue: Command<{
    temperature: CommandParam<RangeConstraints>;
  }>;
}

export enum HeatingConfigurationSecondaryHeatGeneratorModeOnErrorValue {
  reduced = 'reduced',
  normal = 'normal',
}

export interface StatusValueDefaultFeature {
  status: StringValue;
  value?: NumberValue;
}

export interface NumberUnitValueFeature {
  value: {
    type: DcTypes.number;
    unit: string;
    value: number;
  };
}
export interface SecondaryHeatGeneratorProperty {
  connectionType: StringValue;
  active: BooleanValue;
}

export interface SecondaryHeatGeneratorCommands {
  setMode: Command<{
    mode: CommandParam<{
      enum: Array<keyof typeof HybridProPanelHelper.SecondaryHeatGeneratorOperatingModesActive>;
    }>;
  }>;
}

export interface SetTariffNumberCommand {
  setTariff: Command<{
    price: CommandParam<RangeConstraints>;
  }>;
}

export interface SetEnergyFactorCommand {
  setEnergyFactor: Command<{
    factor: CommandParam<RangeConstraints>;
  }>;
}
